package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.OAuthProvider
import fr.manyverse.app.domain.model.User
import fr.manyverse.app.domain.repository.AuthRepository
import fr.manyverse.app.domain.usecase.ClearCharacterCacheUseCase
import fr.manyverse.app.domain.usecase.DeleteAccountUseCase
import fr.manyverse.app.domain.usecase.GetCurrentUserUseCase
import fr.manyverse.app.domain.usecase.SendOtpUseCase
import fr.manyverse.app.domain.usecase.SignInAnonymouslyUseCase
import fr.manyverse.app.domain.usecase.SignInWithOAuthUseCase
import fr.manyverse.app.domain.usecase.SignOutUseCase
import fr.manyverse.app.domain.usecase.VerifyOtpUseCase
import fr.manyverse.app.domain.usecase.userconfig.AcceptTermsUseCase
import fr.manyverse.app.domain.usecase.userconfig.CheckTermsAcceptanceUseCase
import fr.manyverse.app.domain.validation.EmailValidator
import fr.manyverse.app.presentation.error.AuthErrorMapper
import org.jetbrains.compose.resources.StringResource
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AuthUiState(
    val email: String = "",
    val isLoading: Boolean = false,
    val error: StringResource? = null,
    val authenticatedUser: User? = null,
    val isEmailValid: Boolean? = null,
    val isNewLogin: Boolean = false,
    val otpCode: String = "",
    val isOtpSent: Boolean = false,
    val isOAuthPending: Boolean = false,
    val needsTermsAcceptance: Boolean = false,
    val isCheckingTerms: Boolean = false
)

class AuthViewModel(
    private val signOutUseCase: SignOutUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val clearCharacterCacheUseCase: ClearCharacterCacheUseCase,
    private val deleteAccountUseCase: DeleteAccountUseCase,
    private val sendOtpUseCase: SendOtpUseCase,
    private val verifyOtpUseCase: VerifyOtpUseCase,
    private val signInWithOAuthUseCase: SignInWithOAuthUseCase,
    private val signInAnonymouslyUseCase: SignInAnonymouslyUseCase,
    private val authRepository: AuthRepository,
    private val checkTermsAcceptanceUseCase: CheckTermsAcceptanceUseCase,
    private val acceptTermsUseCase: AcceptTermsUseCase
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    private var oauthTimeoutJob: Job? = null

    init {
        // Observe les nouvelles sessions Supabase (callback OAuth async sur JVM/iOS/Android).
        viewModelScope.launch {
            authRepository.observeNewSession().collect { user ->
                oauthTimeoutJob?.cancel()
                _uiState.update {
                    it.copy(
                        authenticatedUser = user,
                        isOAuthPending = false,
                        isLoading = false,
                        error = null
                    )
                }
                checkTermsAfterAuth(user.id, isNewLogin = true)
            }
        }
        // Échec au retour du navigateur : sans cela, l'utilisateur attendrait la fin du délai
        // OAuth sans savoir que sa connexion a échoué
        viewModelScope.launch {
            authRepository.observeOAuthFailures().collect { error ->
                oauthTimeoutJob?.cancel()
                _uiState.update {
                    it.copy(
                        isOAuthPending = false,
                        isLoading = false,
                        error = AuthErrorMapper.toOAuthStringResource(error)
                    )
                }
            }
        }
        // Aligne l'UI quand la session prend fin hors de nos appels : refresh token révoqué ou
        // compte supprimé. Supabase a déjà effacé la session du storage, rien d'autre à purger.
        // Les déconnexions et suppressions de compte passent aussi par ici : même état final.
        viewModelScope.launch {
            authRepository.observeSessionEnded().collect {
                clearCharacterCacheUseCase()
                _uiState.update {
                    it.copy(authenticatedUser = null, isLoading = false, error = null)
                }
            }
        }
    }

    /**
     * Validates email format using centralized EmailValidator.
     */
    private fun isEmailValid(email: String): Boolean {
        return EmailValidator.isValid(email)
    }

    /**
     * Initializes the session by checking for stored session data and validating with Supabase.
     * Should be called on app startup.
     */
    fun initializeSession() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }

            getCurrentUserUseCase().fold(
                onSuccess = { currentUser ->
                    if (currentUser != null) {
                        _uiState.update {
                            it.copy(authenticatedUser = currentUser, isLoading = false)
                        }
                        checkTermsAfterAuth(currentUser.id, isNewLogin = false)
                    } else {
                        _uiState.update {
                            it.copy(authenticatedUser = null, isLoading = false)
                        }
                    }
                },
                onFailure = {
                    _uiState.update {
                        it.copy(authenticatedUser = null, isLoading = false)
                    }
                }
            )
        }
    }

    fun onEmailChange(email: String) {
        _uiState.update {
            it.copy(
                email = email,
                isEmailValid = null,
                error = null
            )
        }
    }

    fun signOut() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }

            // Sign out from Supabase
            val result = signOutUseCase()

            // Clear character cache
            clearCharacterCacheUseCase()

            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            authenticatedUser = null,
                            email = "",
                            error = null,
                            isLoading = false
                        )
                    }
                },
                onFailure = { exception ->
                    // Even if signOut fails, clear local state
                    _uiState.update {
                        it.copy(
                            authenticatedUser = null,
                            email = "",
                            error = AuthErrorMapper.toStringResource(exception),
                            isLoading = false
                        )
                    }
                }
            )
        }
    }

    fun deleteAccount(userId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            clearCharacterCacheUseCase()

            deleteAccountUseCase(userId).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            authenticatedUser = null,
                            email = "",
                            error = null,
                            isLoading = false
                        )
                    }
                },
                onFailure = { exception ->
                    _uiState.update {
                        it.copy(isLoading = false, error = AuthErrorMapper.toStringResource(exception))
                    }
                }
            )
        }
    }

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }

    fun onOtpCodeChange(code: String) {
        _uiState.update { it.copy(otpCode = code, error = null) }
    }

    fun sendOtp() {
        val email = _uiState.value.email
        if (!isEmailValid(email)) {
            _uiState.update { it.copy(isEmailValid = false) }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            sendOtpUseCase(email).fold(
                onSuccess = {
                    _uiState.update { it.copy(isLoading = false, isOtpSent = true) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = AuthErrorMapper.toStringResource(e)) }
                }
            )
        }
    }

    fun verifyOtp() {
        val state = _uiState.value
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            // observeNewSession() reste l'unique source de vérité pour authenticatedUser
            // et checkTermsAfterAuth — on ne fait ici que gérer l'état UI spécifique à l'OTP.
            verifyOtpUseCase(state.email, state.otpCode).fold(
                onSuccess = {
                    _uiState.update { it.copy(isOtpSent = false, otpCode = "") }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = AuthErrorMapper.toStringResource(e)) }
                }
            )
        }
    }

    fun signInAnonymously() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            // observeNewSession() prendra le relais en cas de succès.
            signInAnonymouslyUseCase().onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = AuthErrorMapper.toStringResource(e)) }
            }
        }
    }

    fun signInWithOAuth(provider: OAuthProvider) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            signInWithOAuthUseCase(provider).fold(
                onSuccess = {
                    // Sur JVM, signInWith suspend jusqu'à la fin du flux OAuth → la session
                    // est déjà établie ici. Sur Android, cette session sera nulle (le flux
                    // OAuth n'est pas encore terminé) et initializeSession() sera rappelée
                    // par onOAuthCallbackReceived() après réception du deep link.
                    _uiState.update { it.copy(isLoading = false, isOAuthPending = true) }
                    initializeSession()
                    startOAuthPendingTimeout()
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = AuthErrorMapper.toOAuthStringResource(e)) }
                }
            )
        }
    }

    fun onOAuthCallbackReceived() {
        oauthTimeoutJob?.cancel()
        _uiState.update { it.copy(isOAuthPending = false) }
        initializeSession()
    }

    /**
     * Réinitialise isOAuthPending si l'utilisateur abandonne le flux OAuth (ex: ferme
     * l'onglet du navigateur sans terminer). Sans ce garde-fou, l'état resterait bloqué
     * jusqu'au prochain redémarrage de l'app.
     */
    private fun startOAuthPendingTimeout() {
        oauthTimeoutJob?.cancel()
        oauthTimeoutJob = viewModelScope.launch {
            delay(OAUTH_PENDING_TIMEOUT_MS)
            if (_uiState.value.isOAuthPending) {
                _uiState.update { it.copy(isOAuthPending = false) }
            }
        }
    }

    fun resetState() {
        _uiState.update {
            it.copy(
                email = "",
                otpCode = "",
                isOtpSent = false,
                isOAuthPending = false,
                error = null,
                isLoading = false,
                isEmailValid = null
            )
        }
    }

    fun resetNewLoginFlag() {
        _uiState.update { it.copy(isNewLogin = false) }
    }

    private fun checkTermsAfterAuth(userId: String, isNewLogin: Boolean) {
        viewModelScope.launch {
            _uiState.update { it.copy(isCheckingTerms = true) }
            checkTermsAcceptanceUseCase(userId).fold(
                onSuccess = { accepted ->
                    _uiState.update {
                        it.copy(
                            isCheckingTerms = false,
                            needsTermsAcceptance = !accepted,
                            isNewLogin = if (accepted) isNewLogin else false
                        )
                    }
                },
                onFailure = {
                    _uiState.update { it.copy(isCheckingTerms = false, needsTermsAcceptance = true) }
                }
            )
        }
    }

    fun acceptTerms() {
        val userId = _uiState.value.authenticatedUser?.id ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            acceptTermsUseCase(userId).fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            needsTermsAcceptance = false,
                            isNewLogin = true
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = AuthErrorMapper.toStringResource(e)) }
                }
            )
        }
    }

    fun declineTerms() {
        val userId = _uiState.value.authenticatedUser?.id ?: return
        deleteAccount(userId)
    }

    companion object {
        private const val OAUTH_PENDING_TIMEOUT_MS = 30_000L
    }
}
